import React, { Children, lazy } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarIcon, UserIcon, ClockIcon, ArrowLeftIcon, CalendarCheckIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
const BOOKING_URL = 'https://appointments.4patientcare.app/?CoverKey=5118&Source=website%20WebSchedV4&ReferredBy=Unknon&Gcount=0';
// Full blog post content data
const blogPostsData: Record<string, {
  title: string;
  category: string;
  date: string;
  author: string;
  image: string;
  readTime: string;
  content: React.ReactNode;
}> = {
  'eye-exams': {
    title: 'The Importance of Comprehensive Eye Exams',
    category: 'Eye Health',
    date: 'March 15, 2026',
    author: 'Dr. Nnabue',
    image: 'https://images.unsplash.com/photo-1574482620826-40685ca5ebd2?auto=format&fit=crop&q=80&w=1920',
    readTime: '5 min read',
    content: <>
        <p data-id="element-732">
          Many people believe that if they can see clearly, their eyes are
          healthy. However, clear vision is only one part of the picture. A
          comprehensive eye exam is a vital part of your overall health
          maintenance, offering insights that go far beyond just updating your
          glasses or contact lens prescription.
        </p>
        <h2 data-id="element-733">More Than Just a Vision Check</h2>
        <p data-id="element-734">
          During a comprehensive eye exam, our optometrists evaluate the overall
          health of your eyes. We look at the internal and external structures,
          checking for signs of common eye diseases such as glaucoma, cataracts,
          and macular degeneration. Because many of these conditions develop
          slowly and without early symptoms, regular exams are the only way to
          catch them before they cause irreversible vision loss.
        </p>
        <h2 data-id="element-735">A Window to Your Overall Health</h2>
        <p data-id="element-736">
          Your eyes are the only place in your body where a doctor can see your
          blood vessels in their natural state without surgery. Because of this,
          eye exams can often be the first step in detecting systemic health
          issues, including:
        </p>
        <ul data-id="element-737">
          <li data-id="element-738">Diabetes (diabetic retinopathy)</li>
          <li data-id="element-739">High blood pressure</li>
          <li data-id="element-740">High cholesterol</li>
          <li data-id="element-741">Certain types of cancers and autoimmune diseases</li>
        </ul>
        <h2 data-id="element-742">How Often Should You Get an Exam?</h2>
        <p data-id="element-743">
          The American Optometric Association recommends that most adults have a
          comprehensive eye exam every one to two years. However, if you wear
          corrective lenses, have a family history of eye disease, or have
          health conditions like diabetes, you should be examined annually.
          Children should have their first exam at 6 months, again at age 3, and
          right before starting school.
        </p>
        <p data-id="element-744">
          Don't wait until you experience vision problems to see an eye doctor.
          Preventative care is the best way to ensure a lifetime of healthy
          vision.
        </p>
      </>
  },
  'retinal-screening': {
    title: 'Why Retinal Screening is a Game Changer',
    category: 'Technology',
    date: 'March 02, 2026',
    author: 'Dr. Adaeze Nnabue',
    image: 'https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?auto=format&fit=crop&q=80&w=1920',
    readTime: '4 min read',
    content: <>
        <p data-id="element-745">
          Advancements in medical technology have revolutionized how we approach
          eye care. One of the most significant breakthroughs in recent years is
          advanced retinal screening and imaging. This technology allows us to
          look deeper into the eye than ever before, providing a level of detail
          that traditional examination methods simply cannot match.
        </p>
        <h2 data-id="element-746">What is Retinal Imaging?</h2>
        <p data-id="element-747">
          Retinal imaging uses high-resolution cameras and Optical Coherence
          Tomography (OCT) to take digital pictures of the back of your eye—the
          retina, optic nerve, and blood vessels. It’s a quick, painless, and
          non-invasive process that takes only seconds but provides invaluable
          data about your eye health.
        </p>
        <h2 data-id="element-748">Early Detection Saves Sight</h2>
        <p data-id="element-749">
          The primary benefit of retinal screening is early detection. Many
          vision-threatening diseases begin in the retina and progress silently.
          By the time you notice a change in your vision, significant damage may
          have already occurred. Retinal imaging helps us detect:
        </p>
        <ul data-id="element-750">
          <li data-id="element-751">
            <strong data-id="element-752">Macular Degeneration:</strong> We can spot early signs of
            drusen (tiny yellow deposits) before they affect your central
            vision.
          </li>
          <li data-id="element-753">
            <strong data-id="element-754">Glaucoma:</strong> By analyzing the optic nerve head, we can
            detect the subtle changes associated with glaucoma years before
            vision loss begins.
          </li>
          <li data-id="element-755">
            <strong data-id="element-756">Diabetic Retinopathy:</strong> We can identify microscopic
            leaks in blood vessels caused by elevated blood sugar levels.
          </li>
        </ul>
        <h2 data-id="element-757">A Permanent Baseline</h2>
        <p data-id="element-758">
          Perhaps the most powerful aspect of digital retinal imaging is that it
          creates a permanent historical record of your eyes. Year after year,
          we can compare your current images to past ones side-by-side, allowing
          us to spot microscopic changes over time that might otherwise go
          unnoticed.
        </p>
      </>
  },
  'contact-lenses': {
    title: 'Finding the Perfect Contact Lenses for Your Lifestyle',
    category: 'Eye Care',
    date: 'February 18, 2026',
    author: 'Dr. Nnabue',
    image: 'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&q=80&w=1920',
    readTime: '6 min read',
    content: <>
        <p data-id="element-759">
          Contact lenses offer a fantastic alternative to glasses, providing a
          wider field of view, freedom for active lifestyles, and a natural
          appearance. However, with so many options on the market today, finding
          the right lens can feel overwhelming. The key to success is a
          professional contact lens fitting tailored to your specific needs.
        </p>
        <h2 data-id="element-760">The Fitting Process</h2>
        <p data-id="element-761">
          A contact lens fitting goes beyond a standard eye exam. We measure the
          curvature of your cornea, evaluate your tear film to ensure your eyes
          produce enough moisture to wear lenses comfortably, and discuss your
          lifestyle. Do you work long hours at a computer? Are you an avid
          athlete? Do you want to wear lenses every day or just occasionally?
        </p>
        <h2 data-id="element-762">Types of Contact Lenses</h2>
        <p data-id="element-763">Based on your evaluation, we might recommend:</p>
        <ul data-id="element-764">
          <li data-id="element-765">
            <strong data-id="element-766">Daily Disposables:</strong> The healthiest and most
            convenient option. You wear a fresh pair every day and throw them
            away at night. Perfect for allergy sufferers or occasional wearers.
          </li>
          <li data-id="element-767">
            <strong data-id="element-768">Monthly/Bi-Weekly Lenses:</strong> Cost-effective lenses
            that you clean and store overnight. Modern silicone hydrogel
            materials allow high oxygen flow to keep eyes healthy.
          </li>
          <li data-id="element-769">
            <strong data-id="element-770">Toric Lenses:</strong> Specially designed to correct
            astigmatism, providing stable, crisp vision.
          </li>
          <li data-id="element-771">
            <strong data-id="element-772">Multifocal Lenses:</strong> The contact lens equivalent of
            progressive glasses, allowing you to see clearly at distance,
            intermediate, and near without reading glasses.
          </li>
        </ul>
        <h2 data-id="element-773">Proper Care and Hygiene</h2>
        <p data-id="element-774">
          The most important aspect of wearing contact lenses is proper hygiene.
          Always wash and dry your hands before handling lenses. Never sleep in
          lenses unless specifically approved by your doctor, and never use
          water to clean or store them. Following these simple rules will ensure
          years of comfortable, safe contact lens wear.
        </p>
      </>
  },
  'dry-eye-therapy': {
    title: 'Finding Relief from Chronic Dry Eye',
    category: 'Wellness',
    date: 'February 05, 2026',
    author: 'Dr. Adaeze Nnabue',
    image: 'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?auto=format&fit=crop&q=80&w=1920',
    readTime: '5 min read',
    content: <>
        <p data-id="element-775">
          If you frequently experience eyes that feel gritty, itchy, burning, or
          surprisingly, excessively watery, you might be suffering from Dry Eye
          Syndrome. It’s one of the most common complaints we hear from
          patients, especially in our modern, screen-heavy world.
        </p>
        <h2 data-id="element-776">Understanding the Cause</h2>
        <p data-id="element-777">
          Dry eye occurs when your eyes either don't produce enough tears or
          produce poor-quality tears. Your tear film has three layers: oil,
          water, and mucus. If any of these layers are compromised, your tears
          evaporate too quickly, leaving your eyes exposed and irritated. Common
          causes include:
        </p>
        <ul data-id="element-778">
          <li data-id="element-779">
            Extended digital device use (we blink less when staring at screens)
          </li>
          <li data-id="element-780">Aging and hormonal changes</li>
          <li data-id="element-781">Certain medications (antihistamines, blood pressure meds)</li>
          <li data-id="element-782">Environmental factors (wind, dry air, AC)</li>
          <li data-id="element-783">
            Meibomian Gland Dysfunction (blocked oil glands in the eyelids)
          </li>
        </ul>
        <h2 data-id="element-784">Personalized Treatment Plans</h2>
        <p data-id="element-785">
          Because the causes of dry eye vary, there is no one-size-fits-all
          treatment. Over-the-counter "get the red out" drops often make the
          problem worse in the long run. Instead, we focus on treating the root
          cause.
        </p>
        <p data-id="element-786">
          Treatment options may include prescription eye drops that reduce
          inflammation and increase tear production, punctal plugs (tiny devices
          inserted into tear ducts to keep tears on the eye longer), warm
          compress therapy, and specialized eyelid hygiene routines. We also
          discuss lifestyle and dietary changes, such as increasing Omega-3
          fatty acid intake, to support healthy tear production from the inside
          out.
        </p>
      </>
  },
  'pediatric-eye-care': {
    title: 'When Should Your Child Have Their First Eye Exam?',
    category: 'Pediatric',
    date: 'January 22, 2026',
    author: 'Dr. Adaeze Nnabue',
    image: 'https://images.unsplash.com/photo-1591076482161-42ce6da69f67?auto=format&fit=crop&q=80&w=1920',
    readTime: '4 min read',
    content: <>
        <p data-id="element-787">
          As a parent, you schedule regular pediatrician visits and dental
          checkups for your child, but eye exams are often overlooked until a
          problem becomes obvious. However, vision is intimately tied to the
          learning process. In fact, experts estimate that roughly 80% of what a
          child learns in school is presented visually.
        </p>
        <h2 data-id="element-788">The Recommended Timeline</h2>
        <p data-id="element-789">
          The American Optometric Association recommends the following timeline
          for pediatric eye exams:
        </p>
        <ul data-id="element-790">
          <li data-id="element-791">
            <strong data-id="element-792">6 Months:</strong> The first comprehensive eye exam to
            ensure eyes are developing and aligning properly.
          </li>
          <li data-id="element-793">
            <strong data-id="element-794">3 Years:</strong> To check for visual acuity and rule out
            conditions like amblyopia (lazy eye) or strabismus (crossed eyes).
          </li>
          <li data-id="element-795">
            <strong data-id="element-796">5-6 Years:</strong> Right before starting kindergarten or
            first grade to ensure they have the visual skills needed for reading
            and learning.
          </li>
          <li data-id="element-797">
            <strong data-id="element-798">School Age:</strong> Annually, as children's eyes can change
            rapidly as they grow.
          </li>
        </ul>
        <h2 data-id="element-799">Vision Screenings vs. Eye Exams</h2>
        <p data-id="element-800">
          It's important to understand that the vision screening your child
          receives at school or the pediatrician's office is not a substitute
          for a comprehensive eye exam. Screenings typically only check distance
          vision (the 20/20 line). They often miss issues with eye tracking,
          focusing, color vision, and overall eye health—all of which are
          crucial for reading and classroom learning.
        </p>
        <p data-id="element-801">
          If your child frequently rubs their eyes, complains of headaches,
          holds reading material very close, or loses their place while reading,
          schedule an eye exam immediately. Early intervention is key to
          correcting pediatric vision issues and setting your child up for
          success.
        </p>
      </>
  },
  'lasik-consultation': {
    title: 'Is LASIK Right For You? What to Expect',
    category: 'Surgery',
    date: 'January 10, 2026',
    author: 'Dr. Nnabue',
    image: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&q=80&w=1920',
    readTime: '7 min read',
    content: <>
        <p data-id="element-802">
          Waking up and seeing the alarm clock clearly without reaching for
          glasses or fumbling with contacts is a dream for many. Laser vision
          correction, such as LASIK, has made this a reality for millions. But
          is it the right choice for you? The journey to visual freedom starts
          with a comprehensive LASIK consultation.
        </p>
        <h2 data-id="element-803">Determining Candidacy</h2>
        <p data-id="element-804">
          Not everyone is a candidate for LASIK. During your consultation, we
          perform a series of advanced tests to evaluate your eye health and
          corneal structure. Good candidates typically:
        </p>
        <ul data-id="element-805">
          <li data-id="element-806">Are at least 18 years old (preferably over 21)</li>
          <li data-id="element-807">Have had a stable prescription for at least one to two years</li>
          <li data-id="element-808">Have healthy corneas of adequate thickness</li>
          <li data-id="element-809">Do not have severe dry eye syndrome or active eye diseases</li>
        </ul>
        <p data-id="element-810">
          If LASIK isn't the best option due to thin corneas or a high
          prescription, we will discuss excellent alternatives like PRK
          (Photorefractive Keratectomy) or implantable lenses (ICL).
        </p>
        <h2 data-id="element-811">The Procedure and Recovery</h2>
        <p data-id="element-812">
          The LASIK procedure itself is remarkably quick, usually taking less
          than 15 minutes for both eyes. Numbing drops ensure you are
          comfortable throughout. A specialized laser reshapes your cornea to
          correct nearsightedness, farsightedness, or astigmatism.
        </p>
        <p data-id="element-813">
          Recovery is typically fast. Most patients notice a dramatic
          improvement in their vision within 24 hours and can return to work and
          normal activities the very next day. We provide comprehensive
          post-operative care, monitoring your healing process closely over the
          following weeks and months to ensure optimal results.
        </p>
      </>
  }
};
export function BlogPostPage() {
  const {
    postId
  } = useParams<{
    postId: string;
  }>();
  const post = postId ? blogPostsData[postId] : null;
  if (!post) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center" data-id="element-814">
        <h1 className="text-4xl font-heading font-bold text-brand-dark mb-4" data-id="element-815">
          Article Not Found
        </h1>
        <p className="text-neutral-text/80 mb-8 max-w-md" data-id="element-816">
          We couldn't find the article you're looking for. It may have been
          moved or deleted.
        </p>
        <Link to="/blog" className="inline-flex items-center gap-2 bg-brand-dark text-white px-6 py-3 rounded-button font-heading font-semibold hover:bg-brand-dark/90 transition-colors focus-ring" data-id="element-817">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-818" />
          Back to Blog
        </Link>
      </div>;
  }
  return <div className="bg-white min-h-screen pb-20" data-id="element-819">
      {/* Hero Image Header */}
      <div className="relative h-[40vh] md:h-[50vh] w-full" data-id="element-820">
        <div className="absolute inset-0 bg-brand-dark/40 z-10" data-id="element-821" />
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-822" />
        <div className="absolute inset-0 z-20 flex flex-col justify-end pb-12 md:pb-16" data-id="element-823">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-824">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/90 hover:text-brand-gold transition-colors mb-6 text-sm font-medium focus-ring rounded-sm" data-id="element-825">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-826" />
              Back to all articles
            </Link>
            <div className="mb-4" data-id="element-827">
              <span className="bg-brand-gold text-white text-sm font-bold px-3 py-1 rounded-full shadow-sm" data-id="element-828">
                {post.category}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight drop-shadow-md" data-id="element-829">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-id="element-830">
        {/* Metadata */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-neutral-text/60 font-medium mb-10 pb-8 border-b border-gray-100" data-id="element-831">
          <div className="flex items-center gap-2" data-id="element-832">
            <UserIcon className="w-4 h-4 text-brand-gold" data-id="element-833" />
            {post.author}
          </div>
          <div className="flex items-center gap-2" data-id="element-834">
            <CalendarIcon className="w-4 h-4 text-brand-gold" data-id="element-835" />
            {post.date}
          </div>
          <div className="flex items-center gap-2" data-id="element-836">
            <ClockIcon className="w-4 h-4 text-brand-gold" data-id="element-837" />
            {post.readTime}
          </div>
        </div>

        {/* Body */}
        <motion.article initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="prose prose-lg md:prose-xl max-w-none prose-headings:font-heading prose-headings:font-bold prose-headings:text-brand-dark prose-p:text-neutral-text/80 prose-p:leading-relaxed prose-li:text-neutral-text/80 prose-a:text-brand-gold hover:prose-a:text-brand-gold-light" data-id="element-838">
          {post.content}
        </motion.article>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-16 bg-surface-muted rounded-card p-8 md:p-10 text-center border border-gray-100 shadow-sm" data-id="element-839">
          <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-840">
            <CalendarCheckIcon className="w-8 h-8 text-brand-gold" data-id="element-841" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-brand-dark mb-4" data-id="element-842">
            Ready to Prioritize Your Eye Health?
          </h3>
          <p className="text-neutral-text/80 mb-8 max-w-lg mx-auto" data-id="element-843">
            Schedule an appointment with our experienced team today. We're
            accepting new patients at all 6 of our Maryland locations.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="inline-block bg-brand-dark text-white px-8 py-4 rounded-button font-heading font-bold text-lg hover:bg-brand-dark/90 transition-colors shadow-md focus-ring" data-id="element-844">
            Book Your Appointment Now
          </a>
        </motion.div>
      </main>
    </div>;
}